import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { apiClient } from '../utils/api';
import toast from 'react-hot-toast';

interface OrgShape { name?: string }
interface User {
  id: number;
  email: string;
  phone: string;
  name: string;
  role: 'admin' | 'coordinator' | 'observer';
  district?: string;
  constituency?: string;
  ward?: string;
  organisation_name?: string;      // UK
  organization_name?: string;      // US
  organisation?: OrgShape | null;  // joined object
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (phone: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const STORAGE_TOKEN_KEY = 'token';
const STORAGE_USER_KEY = 'user';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();

  // Optimistic boot from storage
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(STORAGE_TOKEN_KEY));
  const [user, setUser] = useState<User | null>(() => {
    const raw = localStorage.getItem(STORAGE_USER_KEY);
    if (!raw) return null;
    try { return JSON.parse(raw) as User; } catch { return null; }
  });
  const [loading, setLoading] = useState<boolean>(true);

  // Keep axios header in sync
  useEffect(() => {
    if (token) apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    else delete apiClient.defaults.headers.common['Authorization'];
  }, [token]);

  // Centralized setter
  const setAuthState = useCallback((nextUser: User | null, nextToken: string | null) => {
    setUser(nextUser);
    setToken(nextToken);

    if (nextToken) {
      localStorage.setItem(STORAGE_TOKEN_KEY, nextToken);
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${nextToken}`;
    } else {
      localStorage.removeItem(STORAGE_TOKEN_KEY);
      delete apiClient.defaults.headers.common['Authorization'];
    }

    if (nextUser) localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(nextUser));
    else localStorage.removeItem(STORAGE_USER_KEY);
  }, []);

  // Verify / hydrate on mount
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const savedToken = localStorage.getItem(STORAGE_TOKEN_KEY);

        if (savedToken) {
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
          const { data } = await apiClient.get('/auth/me');
          if (cancelled) return;

          const me = data?.data || {};
          const normalized: User = {
            id: me.id,
            email: me.email ?? '',
            phone: me.phone ?? '',
            name: me.name ?? '',
            role: me.role,
            district: me.district ?? '',
            constituency: me.constituency ?? '',
            ward: me.ward ?? '',
            organisation_name: me.organisation_name,
            organization_name: me.organization_name,
            organisation: me.organisation ?? null,
          };

          setAuthState(normalized, savedToken);
        } else {
          // ensure clean state if no token
          setAuthState(null, null);
        }
      } catch {
        // invalid/expired token
        setAuthState(null, null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();
    return () => { cancelled = true; };
  }, [setAuthState]);

  // Cross-tab sync
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_TOKEN_KEY || e.key === STORAGE_USER_KEY) {
        const newToken = localStorage.getItem(STORAGE_TOKEN_KEY);
        const rawUser = localStorage.getItem(STORAGE_USER_KEY);
        const newUser = rawUser ? (() => { try { return JSON.parse(rawUser) as User; } catch { return null; } })() : null;
        setToken(newToken);
        setUser(newUser);
        queryClient.clear();
      }
      if (e.key === STORAGE_TOKEN_KEY && e.newValue === null) {
        Object.keys(localStorage)
          .filter(k => k.startsWith('observerWizardData:'))
          .forEach(k => localStorage.removeItem(k));
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [queryClient]);

  const login = useCallback(async (phone: string, password: string) => {
    // 1) Get token
    const resp = await apiClient.post('/auth/login', { phone, password });
    const tokenFromLogin: string | undefined = resp.data?.data?.token;
    if (!tokenFromLogin) {
      toast.error('Login failed: token missing in response');
      throw new Error('Token missing');
    }

    // 2) Set token BEFORE /auth/me
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${tokenFromLogin}`;
    localStorage.setItem(STORAGE_TOKEN_KEY, tokenFromLogin);

    // 3) Fetch full profile
    const meResp = await apiClient.get('/auth/me');
    const me = meResp.data?.data || {};

    const fullUser: User = {
      id: me.id,
      email: me.email ?? '',
      phone: me.phone ?? '',
      name: me.name ?? '',
      role: me.role,
      district: me.district ?? '',
      constituency: me.constituency ?? '',
      ward: me.ward ?? '',
      organisation_name: me.organisation_name,
      organization_name: me.organization_name,
      organisation: me.organisation ?? null,
    };

    // 4) Persist + cache reset
    setAuthState(fullUser, tokenFromLogin);
    await queryClient.cancelQueries();
    queryClient.clear();

    // 5) Purge other users' drafts
    Object.keys(localStorage)
      .filter(k => k.startsWith('observerWizardData:') && k !== `observerWizardData:${fullUser.id}`)
      .forEach(k => localStorage.removeItem(k));

    toast.success('Login successful!');
  }, [queryClient, setAuthState]);

  const logout = useCallback(() => {
    queryClient.clear();

    // Clear all wizard data from localStorage
    Object.keys(localStorage)
      .filter(k => k.startsWith('observerWizardData'))
      .forEach(k => {
        console.log('Clearing localStorage key:', k);
        localStorage.removeItem(k);
      });

    // Also clear the old general key for backwards compatibility
    localStorage.removeItem('observerWizardData');

    setAuthState(null, null);
    toast.success('Logged out successfully');
  }, [queryClient, setAuthState]);

  const value = useMemo<AuthContextType>(() => ({
    user, token, login, logout, loading
  }), [user, token, login, logout, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
